import { useState, useEffect, useMemo } from 'react'
import {
  Checkbox,
  Box,
  TextField,
  Stack,
  Button,
  FormControlLabel,
  FormGroup,
  Typography,
  styled,
  TypographyProps
} from '@mui/material'

export interface SelectColumnsAutoCompleteProps {
  list:
    | Record<
        string,
        Array<{ field: string; headerName: string; [key: string]: any }>
      >
    | Array<{ field: string; headerName: string; [key: string]: any }>
  defaultValue?: string[]
  onChange?: (values: string[]) => void
}

const StyledFormControlLabel = styled(FormControlLabel)(() => ({
  marginLeft: 0,
  marginRight: 0,
  marginTop: -6,
  marginBottom: -6,
  '& .MuiFormControlLabel-label': {
    fontSize: 14
  },
  transform: 'translateX(-10px)'
}))

const StyledFormGroup = styled(FormGroup)(() => ({
  padding: '4px 0',
  '& .MuiFormGroup-root': {
    padding: '0 0 0 24px'
  }
}))

const SelectColumnsAutoComplete = (props: SelectColumnsAutoCompleteProps) => {
  const { list, defaultValue = [], onChange } = props
  const [showList, setShowList] = useState(list)
  const [selectedValues, setSelectedValues] = useState<string[]>(
    defaultValue || []
  )

  useEffect(() => {
    if (Array.isArray(list) && list.length > 0) {
      setShowList(list)
    }
  }, [list])

  const handleChange = (values: string[]) => {
    setSelectedValues(values)
    onChange?.(values)
  }

  const allListValues = useMemo(() => {
    return Array.isArray(list)
      ? list.map(item => item.field)
      : Object.values(list)
          .flat()
          .map(item => item.field)
  }, [list])

  const onChangeShowList = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    let newShowList
    if (Array.isArray(list)) {
      newShowList = list.filter(item =>
        item.headerName.toLowerCase().includes(value.toLowerCase())
      )
    } else {
      newShowList = Object.entries(list).reduce(
        (acc, [key, subList]) => {
          const filteredSubList = subList.filter(item =>
            item.headerName.toLowerCase().includes(value.toLowerCase())
          )
          if (filteredSubList.length > 0) {
            acc[key] = filteredSubList
          }
          return acc
        },
        {} as Record<
          string,
          Array<{ field: string; headerName: string; [key: string]: any }>
        >
      )
    }

    setShowList(newShowList)
  }

  const handleCheckAll = () => {
    setSelectedValues(allListValues)
    onChange && onChange(allListValues)
  }

  const handleResetAll = () => {
    setSelectedValues([])
    onChange && onChange([])
  }

  useEffect(() => {
    if (defaultValue.length > 0) {
      setSelectedValues(defaultValue)
      onChange && onChange(defaultValue)
    }
  }, [defaultValue])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        padding: '16px 16px 0 16px'
      }}>
      <TextField
        id='outlined-basic'
        label='Find column'
        variant='standard'
        placeholder='column title'
        onChange={onChangeShowList}
        slotProps={{ inputLabel: { shrink: true } }}
      />
      <Box
        sx={{
          maxHeight: 380,
          overflowY: 'auto'
        }}>
        {Array.isArray(showList) ? (
          <Box>
            <StyledFormGroup>
              <FormGroup>
                {showList.map(item => (
                  <StyledFormControlLabel
                    key={item.field}
                    control={
                      <Checkbox
                        size='small'
                        checked={(selectedValues ?? []).includes(item.field)}
                        onChange={e => {
                          const newValues = e.target.checked
                            ? [...(selectedValues ?? []), item.field]
                            : (selectedValues ?? []).filter(
                                v => v !== item.field
                              )
                          handleChange(newValues)
                        }}
                      />
                    }
                    label={item.headerName}
                  />
                ))}
              </FormGroup>
            </StyledFormGroup>
          </Box>
        ) : (
          Object.entries(
            showList as Record<
              string,
              Array<{ field: string; headerName: string; [key: string]: any }>
            >
          ).map(([groupName, fields]) => {
            const groupFields = fields.map(field => field.field)
            const isGroupChecked = groupFields.every(field =>
              (selectedValues ?? []).includes(field)
            )
            const isGroupIndeterminate =
              !isGroupChecked &&
              groupFields.some(field => (selectedValues ?? []).includes(field))

            return (
              <Box key={groupName}>
                <StyledFormGroup>
                  <StyledFormControlLabel
                    control={
                      <Checkbox
                        size='small'
                        checked={isGroupChecked}
                        indeterminate={isGroupIndeterminate}
                        onChange={e => {
                          const newValues = e.target.checked
                            ? [...(selectedValues ?? []), ...groupFields]
                            : (selectedValues ?? []).filter(
                                v => !groupFields.includes(v)
                              )
                          handleChange(newValues)
                        }}
                      />
                    }
                    label={groupName}
                  />
                  <FormGroup>
                    {fields.map(item => (
                      <StyledFormControlLabel
                        key={item.field}
                        control={
                          <Checkbox
                            size='small'
                            checked={(selectedValues ?? []).includes(
                              item.field
                            )}
                            onChange={e => {
                              const newValues = e.target.checked
                                ? [...(selectedValues ?? []), item.field]
                                : (selectedValues ?? []).filter(
                                    v => v !== item.field
                                  )
                              handleChange(newValues)
                            }}
                          />
                        }
                        label={item.headerName}
                      />
                    ))}
                  </FormGroup>
                </StyledFormGroup>
              </Box>
            )
          })
        )}
      </Box>
      <Stack
        direction='row'
        spacing={2}
        justifyContent={'space-between'}
        alignItems={'center'}>
        <Stack
          direction='row'
          spacing={2}
          alignItems={'center'}
          sx={{ transform: 'translateX(-10px)' }}
          justifyContent={'start'}>
          <Checkbox
            checked={selectedValues.length >= allListValues.length}
            onChange={
              selectedValues.length >= allListValues.length
                ? handleResetAll
                : handleCheckAll
            }
            size='small'
          />
          <Typography
            variant={'body6' as TypographyProps['variant']}
            sx={{ ml: '0px!important' }}>
            Show/Hide All
          </Typography>
        </Stack>
        <Button
          color='secondary'
          size='large'
          variant='text'
          sx={{ color: 'secondary.dark', fontWeight: 700 }}
          onClick={handleCheckAll}>
          Reset
        </Button>
      </Stack>
    </Box>
  )
}

export default SelectColumnsAutoComplete
